import { useEffect, useState } from 'react';
import type { Course, Round } from '../lib/types';
import { REGIONS, getRegion, validateHandicapId } from '../../../shared/regions';
import { apiGet, apiPost } from '../lib/api';
import { maybeRequestReview } from '../lib/requestReview';
import { totalsFor } from '../lib/scoring';

interface RegionStatus {
  code: string;
  country: string;
  body: string;
  platform: string;
  idLabel: string;
  idExample: string;
  canPostScores: boolean;
  configured: boolean;
  missing: string[];
  credentials: string;
}

interface Submission {
  id: string;
  player_name: string;
  status: string;
  message: string;
  reference: string | null;
  attempts: number;
}

interface Props {
  course: Course;
  round: Round;
  regionCode: string;
  onRegion: (code: string) => void;
}

const HANDICAP_IDS = 'fairway.handicapIds.v1';

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  pending_credentials: { text: 'HELD', cls: 'warn' },
  queued: { text: 'QUEUED', cls: 'warn' },
  submitted: { text: 'SENT', cls: 'ok' },
  accepted: { text: 'ACCEPTED', cls: 'ok' },
  rejected: { text: 'REJECTED', cls: 'bad' },
  failed: { text: 'RETRYING', cls: 'warn' },
};

/**
 * Post a finished round to the golfer's national handicap body.
 *
 * The region picker is the country selector the client asked for: it decides
 * which federation the score goes to (GOLF Link in Australia, DotGolf in
 * NZ/GB/IE, GHIN in the US) and what the golfer's ID is called.
 */
export default function PostScoreScreen({ course, round, regionCode, onRegion }: Props) {
  const [statuses, setStatuses] = useState<RegionStatus[]>([]);
  const [ids, setIds] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(HANDICAP_IDS) ?? '{}');
    } catch {
      return {};
    }
  });
  const [subs, setSubs] = useState<Submission[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const region = getRegion(regionCode);
  const live = statuses.find((s) => s.code === regionCode);

  useEffect(() => {
    apiGet<RegionStatus[]>('/api/regions')
      .then(setStatuses)
      .catch(() => setStatuses([]));
  }, []);

  const refresh = () =>
    apiGet<Submission[]>(`/api/submissions?round=${encodeURIComponent(round.id)}`)
      .then(setSubs)
      .catch(() => {});

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.id]);

  const setId = (playerId: string, value: string) => {
    const next = { ...ids, [playerId]: value };
    setIds(next);
    localStorage.setItem(HANDICAP_IDS, JSON.stringify(next));
  };

  const post = async () => {
    setBusy(true);
    setNote(null);
    let sent = 0;
    const problems: string[] = [];

    for (const p of round.players) {
      const handicapId = (ids[p.id] ?? '').trim();
      const check = validateHandicapId(regionCode, handicapId);
      if (!check.ok) {
        problems.push(`${p.name}: ${check.message}`);
        continue;
      }
      const card = (round.scores[p.id] ?? []).filter((s) => s.strokes != null);
      if (card.length !== course.holes.length) {
        problems.push(`${p.name}: ${card.length}/${course.holes.length} holes — incomplete cards cannot be posted.`);
        continue;
      }

      const t = totalsFor(round, course, p);
      try {
        await apiPost('/api/submit-score', {
          id: `${round.id}-${p.id}`,
          roundId: round.id,
          regionCode,
          handicapId,
          playerName: p.name,
          courseId: course.id,
          courseName: course.name,
          teeColour: p.teeColour,
          playedAt: round.startedAt,
          format: round.format,
          gross: t.gross,
          holes: course.holes.map((h) => ({
            hole: h.number,
            par: h.par,
            strokeIndex: h.strokeIndex,
            strokes: round.scores[p.id]?.find((s) => s.hole === h.number)?.strokes ?? 0,
          })),
        });
        sent++;
      } catch (e) {
        problems.push(`${p.name}: ${(e as Error).message}`);
      }
    }

    await refresh();
    setBusy(false);
    setNote(
      [sent ? `${sent} card(s) submitted.` : 'Nothing submitted.', ...problems].join(' ')
    );
    // Cards just posted — the moment a rating request is earned, not begged.
    if (sent > 0) void maybeRequestReview();
  };

  return (
    <div className="screen">
      <div className="card">
        <h2>REGION</h2>
        <div className="field">
          <label>Country</label>
          <select value={regionCode} onChange={(e) => onRegion(e.target.value)}>
            {REGIONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.country} — {r.body}
              </option>
            ))}
          </select>
        </div>
        <table className="grid">
          <tbody>
            <tr>
              <td className="name">Handicap body</td>
              <td>{region.body}</td>
            </tr>
            <tr>
              <td className="name">Platform</td>
              <td>{region.platform}</td>
            </tr>
            <tr>
              <td className="name">Score posting</td>
              <td>
                {!region.canPostScores ? (
                  <span className="pill">LOCAL ONLY</span>
                ) : live?.configured ? (
                  <span className="pill ok">CONNECTED</span>
                ) : (
                  <span className="pill warn">NOT YET CONNECTED</span>
                )}
              </td>
            </tr>
            {live && !live.configured && region.canPostScores && (
              <tr>
                <td className="name">Waiting on</td>
                <td className="muted">{live.missing.join(', ') || region.credentials}</td>
              </tr>
            )}
          </tbody>
        </table>
        {region.canPostScores && !live?.configured && (
          <div className="muted" style={{ marginTop: 8 }}>
            Rounds are still saved and will be sent automatically the moment the club's{' '}
            {region.body} credentials are loaded. Nothing needs re-entering.
          </div>
        )}
      </div>

      <div className="card">
        <h2>{region.idLabel.toUpperCase()} PER PLAYER</h2>
        {round.players.map((p) => {
          const v = ids[p.id] ?? '';
          const check = validateHandicapId(regionCode, v);
          return (
            <div className="field" key={p.id}>
              <label>{p.name}</label>
              <input
                value={v}
                placeholder={region.idExample || region.idLabel}
                inputMode="numeric"
                onChange={(e) => setId(p.id, e.target.value)}
              />
              {v && !check.ok && (
                <span className="pill bad" style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                  {check.message}
                </span>
              )}
            </div>
          );
        })}
        <div className="row">
          <button className="btn primary" onClick={post} disabled={busy || !round.players.length}>
            {busy ? 'Submitting…' : `Post to ${region.body}`}
          </button>
          <button className="btn ghost" onClick={() => void refresh()}>
            Refresh status
          </button>
        </div>
        {note && (
          <div className="muted" style={{ marginTop: 8, color: 'var(--accent)' }}>
            {note}
          </div>
        )}
      </div>

      {subs.length > 0 && (
        <div className="card">
          <h2>SUBMISSIONS</h2>
          <table className="grid">
            <thead>
              <tr>
                <th className="name">Player</th>
                <th>Status</th>
                <th>Reference</th>
                <th>Tries</th>
                <th className="name">Detail</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => {
                const label = STATUS_LABEL[s.status] ?? { text: s.status.toUpperCase(), cls: '' };
                return (
                  <tr key={s.id}>
                    <td className="name">{s.player_name}</td>
                    <td>
                      <span className={`pill ${label.cls}`}>{label.text}</span>
                    </td>
                    <td>{s.reference ?? '—'}</td>
                    <td>{s.attempts}</td>
                    <td className="name muted" style={{ whiteSpace: 'normal' }}>
                      {s.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
